/**
 * After generation starts, the client only reads. The server commit owns the
 * article document until enhanced and aiGenerateStatus are done.
 * The one allowed write is the save that starts the run (keywords and the
 * processing flag) before any completion exists to assemble.
 */
import {PARTS_GENERATE_DEADLINE_MS, WHOLE_GENERATE_DEADLINE_MS} from '@/service/partsTerminalState';

export class GenerationSaveBlocked extends Error {
    readonly code = 'generation-in-progress';

    constructor() {
        super('generation-in-progress');
        this.name = 'GenerationSaveBlocked';
    }
}

export function isGenerationSaveBlocked(error: unknown): boolean {
    return !!error && typeof error === 'object' && (error as {code?: string}).code === 'generation-in-progress';
}

/**
 * True while a run may still write the article. A v1.14.2 enhance left
 * enhanced: processing without a status, and a run whose start is older
 * than its limit has nothing left that writes: neither blocks a manual save.
 */
export function generationInProgress(article: {
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
    aiGenerateMode?: string | null;
    ai_content_last_generated_at?: number | null;
} | null | undefined, now: number = Date.now()): boolean {
    if (!article) {
        return false;
    }
    if (article.aiGenerateStatus !== 'running') {
        return false;
    }
    const startedAt = Number(article.ai_content_last_generated_at || 0);
    const limit = article.aiGenerateMode === 'whole' ? WHOLE_GENERATE_DEADLINE_MS : PARTS_GENERATE_DEADLINE_MS;
    if (startedAt > 0 && now - startedAt >= limit) {
        return false;
    }
    return true;
}

/**
 * The run this tab still holds as running is past its limit, so the save is
 * allowed. The watchdog or the server may have ended it failed or done by
 * now, and a tab without a live status watch still has running in memory:
 * such a save leaves the stored generation status alone instead of writing
 * running back over it.
 */
export function savedRunPastLimit(article: Parameters<typeof generationInProgress>[0], now: number = Date.now()): boolean {
    return !!article && article.aiGenerateStatus === 'running' && !generationInProgress(article, now);
}
